// src/contexts/AuthContext.js
import { createContext, useState, useEffect, useContext } from 'react';
import { supabase } from '../supabase';
import { useNavigate } from "react-router-dom";
import { subscribeToResendAudience } from '../hooks/useResendAudience';

const AuthContext = createContext({
  user: null,
  session: null,
  loading: true,
  signIn: async () => {},
  signUp: async () => {},
  signOut: async () => {},
  resendAuthEmail: async () => {},
});

export const AuthProvider = ({ children }) => {
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for active session on initial load
    const getSession = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase.auth.getSession();
        if (error) throw error;
        
        setSession(data.session);
        setUser(data.session?.user || null);
      } catch (error) {
        console.error('Error getting session:', error);
      } finally {
        setLoading(false);
      }
    };

    getSession();

    // Listen for auth changes
    const { data: authListener } = supabase.auth.onAuthStateChange(
      async (event, newSession) => {
        setSession(newSession);
        setUser(newSession?.user || null);
        setLoading(false);
      }
    );

    // Cleanup
    return () => {
      if (authListener && authListener.subscription) {
        authListener.subscription.unsubscribe();
      }
    };
  }, []);



  // Sign in with email and password
  const signIn = async (email, password) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
      
      // Subscribe user to Resend audience upon successful sign-in
      if (data?.user && data?.user.user_metadata.isFirstTimeUser) {
        try {
          await subscribeToResendAudience({
            email: data.user.email,
            firstName: data.user.user_metadata.first_name,
            lastName: data.user.user_metadata.last_name,
          });

          await supabase.auth.updateUser({
            data: { isFirstTimeUser: false },
          });
        } catch (err) {
          console.error("Failed to subscribe user to Resend audience:", err);
        }
      }

      setUser(data.user);
      setSession(data.session);

      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  };

  // Sign up with email and password
  const signUp = async (email, password, userData) => {
    return await supabase.auth.signUp({
      email,
      password,
      options: {
        data: userData,
        emailRedirectTo: "https://doctorsjobs.io/login"
      }
    });
  };

  // Sign out
  const signOut = async () => {
    return await supabase.auth.signOut().then((res) => {
      setUser(null);
      setSession(null);
      setLoading(false);
      navigate("/");
    }).catch((err) => {
      console.log(err);
    });
  };

  // Resend authentication email
  const resendAuthEmail = async (email) => {
    try {
      const { data, error } = await supabase.auth.resend({
        type: 'signup',
        email,
      });
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  };

  const value = {
    user,
    session,
    loading,
    signIn,
    signUp,
    signOut,
    resendAuthEmail,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Custom hook to use the auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext; 